import { Handle, type NodeProps, Position } from "@xyflow/react";
import { Tag } from "antd";
import { memo, useCallback } from "react";
import { color, font, radius } from "theme/aiInventory";

import type { Risk, RiskSeverity } from "../types.ts";

export type RiskNodeData = {
  risk: Risk;
  /** Lifted to SidecarGraph so layout can reserve extra height when true. */
  expanded?: boolean;
  onToggle?: () => void;
};

type SeverityPalette = {
  bg: string;
  ink: string;
  ribbon: string;
};

const SEVERITY_PALETTE: Record<RiskSeverity, SeverityPalette> = {
  urgent: {
    bg: color.riskUrgentBg,
    ink: color.riskUrgent,
    ribbon: color.riskUrgent,
  },
  high: { bg: color.riskHighBg, ink: color.riskHigh, ribbon: color.riskHigh },
  medium: {
    bg: color.riskMediumBg,
    ink: color.riskMedium,
    ribbon: color.riskMedium,
  },
  low: { bg: color.riskLowBg, ink: color.riskLow, ribbon: color.riskLow },
};

const base: React.CSSProperties = {
  borderRadius: radius.card,
  padding: "8px 14px",
  fontFamily: font.sans,
  fontSize: 12,
  width: 260,
  textAlign: "left",
  position: "relative",
  cursor: "pointer",
  userSelect: "none",
};

const headerRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const ribbonStyle = (c: string): React.CSSProperties => ({
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  width: 3,
  background: c,
  borderTopLeftRadius: radius.card,
  borderBottomLeftRadius: radius.card,
});

const severityChip = (ink: string): React.CSSProperties => ({
  fontFamily: font.mono,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: ink,
  padding: "1px 6px",
  border: `1px solid ${ink}`,
  borderRadius: radius.sharp,
  flexShrink: 0,
  lineHeight: 1.4,
});

const standardsRow: React.CSSProperties = {
  marginTop: 8,
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
};

const standardTagStyle: React.CSSProperties = {
  fontFamily: font.mono,
  fontSize: 10,
  margin: 0,
  padding: "0 6px",
  lineHeight: 1.6,
};

export const RiskNode: React.FC<NodeProps> = memo(({ data }) => {
  const typed = data as RiskNodeData;
  const { risk, expanded = false, onToggle } = typed;
  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggle?.();
    },
    [onToggle]
  );

  const p = SEVERITY_PALETTE[risk.severity];
  const nodeStyle: React.CSSProperties = {
    ...base,
    background: p.bg,
    border: `1px solid ${p.ink}`,
    color: color.ink,
  };

  return (
    <div
      style={nodeStyle}
      data-testid="risk-node"
      onClick={handleToggle}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
    >
      <Handle type="target" position={Position.Left} />
      <span style={ribbonStyle(p.ribbon)} aria-hidden />
      <div style={headerRow}>
        <span style={severityChip(p.ink)}>{risk.severity}</span>
        <span
          style={{
            fontWeight: 500,
            minWidth: 0,
            flex: 1,
            ...(expanded
              ? { whiteSpace: "normal", lineHeight: 1.35 }
              : {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }),
          }}
          title={expanded ? undefined : risk.title}
        >
          {risk.title}
        </span>
      </div>
      {expanded && risk.description && (
        <div
          style={{
            marginTop: 8,
            color: color.inkMute,
            lineHeight: 1.4,
          }}
        >
          {risk.description}
        </div>
      )}
      {risk.standards && (
        <div style={standardsRow}>
          {risk.standards.owaspAsi.map((code) => (
            <Tag key={`asi-${code}`} color="default" style={standardTagStyle}>
              {code}
            </Tag>
          ))}
          {risk.standards.nistAiRmf.map((code) => (
            <Tag key={`nist-${code}`} color="default" style={standardTagStyle}>
              {code}
            </Tag>
          ))}
        </div>
      )}
      <Handle type="source" position={Position.Right} />
    </div>
  );
});
RiskNode.displayName = "RiskNode";
